import { useEffect, useRef, useState } from "react";

export type RecorderStatus = "idle" | "recording" | "paused";

/** Backend accepts audio up to 50 MB. The size is checked after recording stops. */
export const MAX_VOICE_BYTES = 50 * 1024 * 1024;

export interface UseVoiceRecorderReturn {
  status: RecorderStatus;
  elapsedMs: number;
  previewUrl: string | null;
  previewBuffer: AudioBuffer | null;
  previewPeaks: number[];
  canResume: boolean;
  start: () => Promise<void>;
  pause: () => void;
  resume: () => void;
  stop: () => Promise<File>;
  reset: () => void;
}

function supportedMime() {
  if (typeof MediaRecorder === "undefined") {
    return { mimeType: "", extension: "webm" };
  }

  if (MediaRecorder.isTypeSupported("audio/webm;codecs=opus")) {
    return { mimeType: "audio/webm;codecs=opus", extension: "webm" };
  }

  if (MediaRecorder.isTypeSupported("audio/mp4")) {
    return { mimeType: "audio/mp4", extension: "m4a" };
  }

  return { mimeType: "", extension: "webm" };
}

export function useVoiceRecorder(): UseVoiceRecorderReturn {
  const [status, setStatus] = useState<RecorderStatus>("idle");
  const [elapsedMs, setElapsedMs] = useState(0);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewBuffer, setPreviewBuffer] = useState<AudioBuffer | null>(null);
  const [previewPeaks, setPreviewPeaks] = useState<number[]>([]);
  const [canResume, setCanResume] = useState(false);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const accumulatedMsRef = useRef(0);
  const startedAtRef = useRef<number | null>(null);
  const timerRef = useRef<number | null>(null);
  const fileRef = useRef<File | null>(null);
  const stopResolveRef = useRef<((file: File) => void) | null>(null);
  const previewUrlRef = useRef<string | null>(null);
  const wantsPreviewRef = useRef(false);
  const startingRef = useRef(false);
  const sessionRef = useRef(0);
  const mimeRef = useRef(supportedMime());
  const pcmRef = useRef<Float32Array[]>([]);
  const captureRef = useRef<{
    context: AudioContext;
    source: MediaStreamAudioSourceNode;
    processor: ScriptProcessorNode;
    analyser: AnalyserNode;
    mute: GainNode;
  } | null>(null);
  const visualPeaksRef = useRef<number[]>([]);
  const peakTimerRef = useRef<number | null>(null);
  const capturingRef = useRef(false);

  const replacePreview = (url: string | null) => {
    /*
     * The previous blob URL is revoked on a delay instead of immediately:
     * `stop()` publishes a second preview while the audio player may still be
     * fetching the first one, and revoking mid-request aborts the decode so the
     * waveform stays empty until the next attempt.
     */
    const previous = previewUrlRef.current;
    previewUrlRef.current = url;
    setPreviewUrl(url);
    if (previous) {
      window.setTimeout(() => URL.revokeObjectURL(previous), 10_000);
    }
  };

  const clearTimer = () => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  };

  const stopPeakTimer = () => {
    if (peakTimerRef.current === null) return;
    window.clearInterval(peakTimerRef.current);
    peakTimerRef.current = null;
  };

  const startPeakTimer = (analyser: AnalyserNode) => {
    stopPeakTimer();
    const data = new Float32Array(analyser.fftSize);
    peakTimerRef.current = window.setInterval(() => {
      analyser.getFloatTimeDomainData(data);
      let peak = 0;
      for (let index = 0; index < data.length; index += 1) {
        const value = Math.abs(data[index]);
        if (value > peak) peak = value;
      }
      visualPeaksRef.current.push(peak);
    }, 40);
  };

  const stopCapture = () => {
    stopPeakTimer();
    const capture = captureRef.current;
    if (!capture) return;
    capture.processor.onaudioprocess = null;
    capture.processor.disconnect();
    capture.analyser.disconnect();
    capture.source.disconnect();
    capture.mute.disconnect();
    void capture.context.close();
    captureRef.current = null;
  };

  const onPcm = (event: AudioProcessingEvent) => {
    if (!capturingRef.current) return;
    const left = event.inputBuffer.getChannelData(0);
    const right =
      event.inputBuffer.numberOfChannels > 1
        ? event.inputBuffer.getChannelData(1)
        : left;
    const mixed = new Float32Array(left.length);
    for (let index = 0; index < left.length; index += 1) {
      mixed[index] =
        Math.abs(left[index]) > Math.abs(right[index])
          ? left[index]
          : right[index];
    }
    pcmRef.current.push(mixed);
  };

  const connectProcessor = (
    source: MediaStreamAudioSourceNode,
    mute: GainNode,
  ) => {
    const processor = source.context.createScriptProcessor(4096, 2, 1);
    processor.onaudioprocess = onPcm;
    source.connect(processor);
    processor.connect(mute);
    return processor;
  };

  const startCapture = (stream: MediaStream, context: AudioContext) => {
    void context.resume();
    const source = context.createMediaStreamSource(stream);
    const analyser = context.createAnalyser();
    analyser.fftSize = 256;
    const mute = context.createGain();
    mute.gain.value = 0;
    source.connect(analyser);
    mute.connect(context.destination);
    const processor = connectProcessor(source, mute);
    captureRef.current = { context, source, processor, analyser, mute };
    visualPeaksRef.current = [];
    capturingRef.current = true;
    startPeakTimer(analyser);
  };

  const buildPreviewBuffer = () => {
    const capture = captureRef.current;
    const chunks = pcmRef.current;
    if (!capture || chunks.length === 0) {
      setPreviewBuffer(null);
      return;
    }
    const length = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
    const buffer = capture.context.createBuffer(
      1,
      length,
      capture.context.sampleRate,
    );
    const data = buffer.getChannelData(0);
    let offset = 0;
    for (const chunk of chunks) {
      data.set(chunk, offset);
      offset += chunk.length;
    }
    setPreviewBuffer(buffer);
  };

  const buildPreviewPeaks = () => {
    const raw = visualPeaksRef.current;
    if (raw.length === 0) {
      setPreviewPeaks([]);
      return;
    }
    const loudest = Math.max(...raw);
    if (loudest === 0) {
      setPreviewPeaks(raw);
      return;
    }
    setPreviewPeaks(raw.map((peak) => Math.sqrt(peak / loudest)));
  };

  const publishPreview = () => {
    if (chunksRef.current.length === 0) return;
    const blob = new Blob(chunksRef.current, {
      type: mimeRef.current.mimeType || "audio/webm",
    });
    replacePreview(URL.createObjectURL(blob));
  };

  const makeFile = () => {
    const { mimeType, extension } = mimeRef.current;
    const blob = new Blob(chunksRef.current, {
      type: mimeType || "audio/webm",
    });
    const stamp = new Date().toISOString().replace(/[:.]/g, "-");
    return new File([blob], `voice-${stamp}.${extension}`, {
      type: mimeType || "audio/webm",
    });
  };

  const snapshotElapsed = () => {
    if (startedAtRef.current === null) return accumulatedMsRef.current;
    const total = accumulatedMsRef.current + Date.now() - startedAtRef.current;
    startedAtRef.current = null;
    accumulatedMsRef.current = total;
    setElapsedMs(total);
    return total;
  };

  const startTicker = () => {
    timerRef.current = window.setInterval(() => {
      if (startedAtRef.current === null) return;
      const total =
        accumulatedMsRef.current + Date.now() - startedAtRef.current;
      setElapsedMs(total);
    }, 100);
  };

  const cleanup = () => {
    sessionRef.current += 1;
    clearTimer();
    wantsPreviewRef.current = false;
    stopResolveRef.current = null;

    const recorder = recorderRef.current;
    recorderRef.current = null;
    if (recorder && recorder.state !== "inactive") {
      recorder.onstop = null;
      recorder.ondataavailable = null;
      recorder.stop();
    }

    capturingRef.current = false;
    stopCapture();
    stopStream();
    pcmRef.current = [];
    visualPeaksRef.current = [];
    chunksRef.current = [];
    fileRef.current = null;
    accumulatedMsRef.current = 0;
    startedAtRef.current = null;
    replacePreview(null);
    setPreviewBuffer(null);
    setPreviewPeaks([]);
    setElapsedMs(0);
    setCanResume(false);
    setStatus("idle");
  };

  const cleanupRef = useRef(cleanup);
  cleanupRef.current = cleanup;

  useEffect(() => {
    return () => cleanupRef.current();
  }, []);

  const start = async () => {
    if (startingRef.current) return;
    startingRef.current = true;
    cleanup();
    const session = sessionRef.current;

    try {
      mimeRef.current = supportedMime();
      const { mimeType } = mimeRef.current;
      const context = new AudioContext();
      void context.resume();
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      if (sessionRef.current !== session) {
        stream.getTracks().forEach((track) => track.stop());
        void context.close();
        return;
      }

      streamRef.current = stream;
      pcmRef.current = [];
      startCapture(stream, context);
      const recorder = new MediaRecorder(
        stream,
        mimeType ? { mimeType } : undefined,
      );
      recorderRef.current = recorder;
      chunksRef.current = [];
      fileRef.current = null;

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
        if (!wantsPreviewRef.current || chunksRef.current.length === 0) return;
        wantsPreviewRef.current = false;
        buildPreviewBuffer();
        buildPreviewPeaks();
        publishPreview();
      };

      recorder.onstop = () => {
        stopPeakTimer();
        buildPreviewBuffer();
        buildPreviewPeaks();
        stopCapture();
        stopStream();
        const file = makeFile();
        fileRef.current = file;
        publishPreview();
        const resolve = stopResolveRef.current;
        stopResolveRef.current = null;
        resolve?.(file);
      };

      accumulatedMsRef.current = 0;
      startedAtRef.current = Date.now();
      setElapsedMs(0);
      setCanResume(false);
      recorder.start();
      setStatus("recording");
      startTicker();
    } catch (error) {
      if (sessionRef.current === session) cleanup();
      throw error;
    } finally {
      startingRef.current = false;
    }
  };

  const pause = () => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state !== "recording") return;

    snapshotElapsed();
    clearTimer();
    capturingRef.current = false;
    stopPeakTimer();
    buildPreviewBuffer();
    buildPreviewPeaks();
    wantsPreviewRef.current = true;
    recorder.pause();
    try {
      recorder.requestData();
    } catch {
      wantsPreviewRef.current = false;
    }
    setCanResume(true);
    setStatus("paused");
  };

  const resume = () => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state !== "paused") return;

    replacePreview(null);
    capturingRef.current = true;
    const capture = captureRef.current;
    if (capture) {
      capture.processor.onaudioprocess = null;
      capture.processor.disconnect();
      capture.processor = connectProcessor(capture.source, capture.mute);
      void capture.context.resume().then(() => {
        if (!capturingRef.current || captureRef.current !== capture) return;
        startPeakTimer(capture.analyser);
      });
    }
    recorder.resume();
    startedAtRef.current = Date.now();
    setStatus("recording");
    startTicker();
  };

  const stop = () => {
    if (fileRef.current) return Promise.resolve(fileRef.current);

    const recorder = recorderRef.current;
    if (!recorder || recorder.state === "inactive") {
      return Promise.reject(new Error("Recorder is not active"));
    }

    snapshotElapsed();
    clearTimer();
    setCanResume(false);
    setStatus("paused");

    return new Promise<File>((resolve) => {
      stopResolveRef.current = resolve;
      recorder.stop();
    });
  };

  return {
    status,
    elapsedMs,
    previewUrl,
    previewBuffer,
    previewPeaks,
    canResume,
    start,
    pause,
    resume,
    stop,
    reset: cleanup,
  };
}
